import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { StatusChangeDialog } from "@/components/StatusChangeDialog";
import { STATUS, STATUS_ORDER, fmtEUR, relativeDays } from "@/lib/constants";
import { Loader2, Phone, GripVertical } from "lucide-react";
import { cn } from "@/lib/utils";

export default function Pipeline() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [pendingLead, setPendingLead] = useState(null);

  const { data: leads = [], isLoading } = useQuery({
    queryKey: ["leads", "sort=created_desc"],
    queryFn: () => api.get("/leads?sort=created_desc").then((r) => r.data),
  });

  const byStatus = (s) => leads.filter((l) => l.status === s);

  const doMove = async (leadId, newStatus) => {
    try {
      await api.patch(`/leads/${leadId}/status`, { status: newStatus });
      toast.success(`Verschoben nach „${STATUS[newStatus].label}“`);
      qc.invalidateQueries();
    } catch {
      toast.error("Fehler beim Verschieben");
    }
  };

  const onDrop = (status) => {
    setDragOver(null);
    if (!dragId) return;
    const lead = leads.find((l) => l.id === dragId);
    setDragId(null);
    if (!lead || lead.status === status) return;
    if (status === "verloren" || status === "kunde") {
      setPendingLead({ ...lead, status });
      return;
    }
    doMove(lead.id, status);
  };

  if (isLoading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 fade-in h-full flex flex-col">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Sales Pipeline</h1>
        <p className="text-sm text-muted-foreground mt-1">Karten per Drag &amp; Drop verschieben — Status wird automatisch gespeichert.</p>
      </div>

      <div className="flex-1 overflow-x-auto pb-4">
        <div className="flex gap-4 min-w-max h-full">
          {STATUS_ORDER.map((s) => {
            const items = byStatus(s);
            const sum = items.reduce((a, l) => a + (l.potential_value || 0), 0);
            return (
              <div key={s} data-testid={`kanban-col-${s}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(s); }}
                onDragLeave={() => setDragOver((v) => (v === s ? null : v))}
                onDrop={() => onDrop(s)}
                className={cn("w-72 flex-shrink-0 rounded-xl border border-border bg-card/40 flex flex-col",
                  dragOver === s && "kanban-drag-over")}>
                <div className="p-3 border-b border-border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={cn("h-2 w-2 rounded-full", STATUS[s].dot)} />
                      <span className="font-semibold text-sm">{STATUS[s].label}</span>
                      <span className="text-xs text-muted-foreground">{items.length}</span>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{fmtEUR(sum)}</div>
                </div>
                <div className="p-2 space-y-2 overflow-y-auto flex-1 min-h-[120px]">
                  {items.map((l) => (
                    <div key={l.id} draggable data-testid={`kanban-card-${l.id}`}
                      onDragStart={() => setDragId(l.id)}
                      onDragEnd={() => { setDragId(null); setDragOver(null); }}
                      onClick={() => navigate(`/leads/${l.id}`)}
                      className={cn("group rounded-lg border border-border bg-card p-3 cursor-grab active:cursor-grabbing hover:border-primary/40 transition-colors",
                        dragId === l.id && "opacity-50")}>
                      <div className="flex items-start gap-1.5">
                        <GripVertical className="h-4 w-4 text-muted-foreground/40 mt-0.5 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-sm truncate">{l.company}</div>
                          <div className="text-xs text-muted-foreground truncate">{l.industry || "—"}</div>
                          {l.contact_person && <div className="text-xs text-muted-foreground truncate">{l.contact_person}</div>}
                          <div className="mt-2 flex items-center justify-between">
                            <span className="text-xs font-semibold text-sky-400">{fmtEUR(l.potential_value)}</span>
                            {l.last_contact_at && <span className="text-[11px] text-muted-foreground">{relativeDays(l.last_contact_at)}</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {items.length === 0 && (
                    <div className="text-center text-xs text-muted-foreground/50 py-6">Leer</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {pendingLead && (
        <StatusChangeDialog open={!!pendingLead} onOpenChange={(o) => !o && setPendingLead(null)}
          lead={pendingLead} onSaved={() => setPendingLead(null)} />
      )}
    </div>
  );
}
